import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "./api";
import { Card, PageHead, Pill, Confirm, Btn } from "./widgets";
import { useAdmin } from "./AdminApp";
import { toast } from "sonner";

const MODES = [
  { key: "public", label: "Public", desc: "Anyone can access the website." },
  { key: "private", label: "Private Preview", desc: "Public sees the construction page; clients enter with the preview password." },
  { key: "maintenance", label: "Maintenance", desc: "Website paused — construction page shown to everyone." },
];

const Dashboard = () => {
  const { user, can } = useAdmin();
  const [data, setData] = useState(null);
  const [confirmMode, setConfirmMode] = useState(null);

  const load = () =>
    api.get("/admin/dashboard").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));

  useEffect(() => {
    load();
  }, []);

  const changeMode = async (mode) => {
    try {
      await api.patch("/admin/settings", { accessMode: mode });
      toast.success(`Website is now ${mode === "public" ? "PUBLIC" : mode === "private" ? "in PRIVATE PREVIEW" : "in MAINTENANCE"}`);
      setConfirmMode(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
      setConfirmMode(null);
    }
  };

  if (!data) return <p className="text-sm text-[#171717]/50">Loading…</p>;

  const cards = [
    { label: "New Leads", value: data.newLeads, to: "/admin/leads", perm: "leads" },
    { label: "Unread Messages", value: data.unreadMessages, to: "/admin/messages", perm: "messages" },
    { label: "Consultation Requests", value: data.consultationRequests, to: "/admin/consultations", perm: "consultations" },
    { label: "Visitors Today", value: data.visitorsToday, to: "/admin/analytics", perm: "analytics" },
    { label: "Visitors (30 days)", value: data.visitorsMonth, to: "/admin/analytics", perm: "analytics" },
    { label: "Published Articles", value: data.publishedPosts, to: "/admin/blog", perm: "blog" },
    { label: "Draft Articles", value: data.draftPosts, to: "/admin/blog", perm: "blog" },
  ].filter((c) => can(c.perm));

  const quickActions = [
    { label: "Edit Website", to: "/admin/website", perm: "content" },
    { label: "New Blog", to: "/admin/blog", perm: "blog" },
    { label: "Upload Media", to: "/admin/media", perm: "media" },
    { label: "View Leads", to: "/admin/leads", perm: "leads" },
  ].filter((a) => can(a.perm));

  const modeTone = { public: "green", private: "champagne", maintenance: "red" };

  return (
    <div data-testid="admin-dashboard">
      <PageHead title={`Welcome, ${user.name.split(" ")[0]}.`} sub="Aviah Financial — private control center" />

      <Card className="mb-6 border-l-2 border-l-[#C6A46A]" data-testid="website-status-card">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-[0.62rem] font-medium uppercase tracking-[0.18em] text-[#171717]/50">Website Status</p>
            <div className="mt-2 flex items-center gap-3">
              <Pill tone={modeTone[data.accessMode]}>{data.accessMode}</Pill>
              <span className="text-sm text-[#171717]/60">
                {MODES.find((m) => m.key === data.accessMode)?.desc}
              </span>
            </div>
          </div>
          {user.role === "super_admin" && (
            <div className="flex gap-2">
              {MODES.filter((m) => m.key !== data.accessMode).map((m) => (
                <Btn key={m.key} onClick={() => setConfirmMode(m)} data-testid={`status-${m.key}`}>
                  Switch to {m.label}
                </Btn>
              ))}
            </div>
          )}
        </div>
      </Card>

      <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
        {cards.map((c) => (
          <Link key={c.label} to={c.to} data-testid={`dash-card-${c.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
            <Card className="h-full transition-colors hover:border-[#C6A46A]/60">
              <p className="text-[0.6rem] font-medium uppercase tracking-[0.16em] text-[#171717]/50">{c.label}</p>
              <p className="mt-3 text-3xl font-light text-[#171717] sm:text-4xl">{c.value}</p>
            </Card>
          </Link>
        ))}
      </div>

      {quickActions.length > 0 && (
        <>
          <p className="mb-3 mt-8 text-[0.62rem] font-medium uppercase tracking-[0.18em] text-[#171717]/50">Quick Actions</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {quickActions.map((a) => (
              <Link
                key={a.label}
                to={a.to}
                data-testid={`quick-${a.label.toLowerCase().replace(/\s/g, "-")}`}
                className="flex min-h-[52px] items-center justify-center rounded-[3px] border border-[#171717]/15 bg-white px-3 py-3 text-center text-[0.62rem] font-medium uppercase tracking-[0.14em] text-[#171717]/70 transition-colors hover:border-[#C6A46A] hover:text-[#8a6f3e]"
              >
                {a.label}
              </Link>
            ))}
          </div>
        </>
      )}

      <Confirm
        open={Boolean(confirmMode)}
        title={`Switch website to ${confirmMode?.label}?`}
        body={
          confirmMode?.key === "public"
            ? "Are you sure you want to make AviahFinancial.com public? Anyone will be able to access it."
            : `Are you sure you want to place AviahFinancial.com in ${confirmMode?.label} mode? Public visitors will see the construction page.`
        }
        confirmLabel="Yes, switch"
        onConfirm={() => changeMode(confirmMode.key)}
        onCancel={() => setConfirmMode(null)}
      />
    </div>
  );
};

export default Dashboard;
